/**
 * The enrolment trigger's own settings, on the trigger card: who it goes
 * to (a segment built here from streams, a rolling "arrived in the last N
 * days" and field rules, or a saved one) and when (now, once at a UK date
 * and time, every day or every week). This is how "Email a list once"
 * works: a send-once schedule on an automation whose step sends an email.
 *
 * Before anything is set, the preview counts it as a send would read it:
 * those that agreed to emails, one per address, less the never-email list
 * and failed checks. A once send with an email step waits for its
 * addresses to be checked ("Check addresses", or free checks only as the
 * override) and can be cancelled before it fires or while it sends.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Collapse, Group, Loader, MultiSelect, NumberInput, SegmentedControl, Select,
  SimpleGrid, Stack, Switch, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalendarEvent, IconChecks, IconSend, IconX } from '@tabler/icons-react';
import { useCampaigns } from '../../api/queries';
import {
  useEnrolEdits, useEnrolments, useEnrolPreview, type EnrolBody, type EnrolPreview, type RecordType,
  type WorkflowEnrolSchedule,
} from '../../api/workflows';
import { formatNumber } from '../../theme';
import { useVocab } from '../../vocab';
import { PreflightModal } from '../emails/PreflightModal';
import { RuleBuilder } from '../LeadsFilterPanel';
import { keyOf, useSegmentChoices } from './EnrolDialog';
import {
  BLANK_SEGMENT, MAX_DAYS, builtFilter, onceWords, ukDate, ukNow, type BuiltSegment,
} from './sendOnce';

type When = 'now' | 'once' | 'daily' | 'weekly';
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_OK = /^([01]?\d|2[0-3]):[0-5]\d$/;
const DATE_OK = /^\d{4}-\d{2}-\d{2}$/;

export function SendToSegment({
  workflowId, enabled, sendsEmail, recordType, dirty, canManage, oneOff = false,
}: {
  /** The saved workflow; null while it is new. */
  workflowId: string | null;
  enabled: boolean;
  sendsEmail: boolean;
  recordType: RecordType;
  dirty: boolean;
  canManage: boolean;
  /** A bulk send: now or once only, and no new send once one is set. */
  oneOff?: boolean;
}) {
  if (!workflowId) {
    return (
      <Text size="xs" c="dimmed" data-testid="send-needs-save">
        Make it first, then choose who it goes to and when, here.
      </Text>
    );
  }
  return (
    <Stack gap="sm" data-testid="send-to-segment">
      <Schedules workflowId={workflowId} sendsEmail={sendsEmail} canManage={canManage} />
      {canManage && (
        <OneOffGate workflowId={workflowId} oneOff={oneOff}>
          <NewSend workflowId={workflowId} enabled={enabled} sendsEmail={sendsEmail} recordType={recordType} dirty={dirty} oneOff={oneOff} />
        </OneOffGate>
      )}
    </Stack>
  );
}

/**
 * A bulk send sends once: while one of its sends is set or has gone, there
 * is no form for another (Duplicate makes a fresh one to send again).
 */
function OneOffGate({ workflowId, oneOff, children }: { workflowId: string; oneOff: boolean; children: React.ReactNode }) {
  const data = useEnrolments(oneOff ? workflowId : null).data;
  if (!oneOff) return <>{children}</>;
  if (!data) return null;
  const set = data.schedules.some((s) => s.frequency === 'once' && (s.status === 'active' || s.status === 'done'))
    || data.enrolments.length > 0;
  if (set) {
    return (
      <Text size="xs" c="dimmed" data-testid="one-off-set">
        This bulk send is set. To send it again, duplicate it.
      </Text>
    );
  }
  return <>{children}</>;
}

/** What a schedule is doing, as a badge. */
function scheduleBadge(s: WorkflowEnrolSchedule, sending: boolean): { label: string; color: string } {
  if (s.status === 'cancelled') return { label: 'Cancelled', color: 'gray' };
  if (s.frequency !== 'once') return { label: 'Repeats', color: 'blue' };
  if (s.status === 'done') return sending ? { label: 'Sending', color: 'teal' } : { label: 'Sent', color: 'gray' };
  if (s.waiting_for_check) return { label: 'Waiting for its address check', color: 'chartAmber' };
  return { label: 'Scheduled', color: 'blue' };
}

function Schedules({ workflowId, sendsEmail, canManage }: { workflowId: string; sendsEmail: boolean; canManage: boolean }) {
  const data = useEnrolments(workflowId).data;
  const edits = useEnrolEdits();
  const [checking, setChecking] = useState<WorkflowEnrolSchedule | null>(null);
  const schedules = (data?.schedules ?? []).filter((s) => s.status !== 'cancelled');
  const open = new Set(
    (data?.enrolments ?? []).filter((e) => e.status === 'queued' || e.status === 'running').map((e) => e.schedule_id),
  );
  if (schedules.length === 0) return null;

  async function cancel(s: WorkflowEnrolSchedule) {
    const once = s.frequency === 'once';
    const sure = once
      ? window.confirm(s.status === 'done'
        ? 'Stop this send? Emails already sent stay sent; the rest are not sent.'
        : 'Cancel this send? Nothing is sent.')
      : window.confirm('Stop this schedule? Nobody more is enrolled by it.');
    if (!sure) return;
    try {
      if (once) await edits.cancelSchedule.mutateAsync({ id: workflowId, scheduleId: s.id });
      else await edits.stopSchedule.mutateAsync({ id: workflowId, scheduleId: s.id });
      notifications.show({ color: 'teal', title: once ? 'Cancelled' : 'Stopped', message: s.sentence });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not cancelled', message: (e as Error).message });
    }
  }

  return (
    <Stack gap={6} data-testid="schedules">
      {schedules.map((s) => {
        const sending = open.has(s.id);
        const badge = scheduleBadge(s, sending);
        const once = s.frequency === 'once';
        const cancellable = s.status === 'active' || (once && sending);
        const checked = s.verify_status === 'done' ? 'Addresses checked' : s.verify_status === 'override' ? 'Free checks only' : '';
        return (
          <Card key={s.id} padding="xs" withBorder data-testid={`schedule-${s.id}`}>
            <Group justify="space-between" gap="xs" wrap="wrap">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={6} wrap="wrap">
                  <Text size="sm" fw={600}>{once && s.at ? `Once, ${onceWords(s.at)}` : s.sentence}</Text>
                  <Badge size="sm" variant="light" color={badge.color} tt="none" data-testid="schedule-status">{badge.label}</Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  {`${s.segment_name}${once && sendsEmail && checked ? `. ${checked}.` : ''}`}
                </Text>
              </Stack>
              {canManage && (
                <Group gap={6}>
                  {once && sendsEmail && s.status === 'active' && (
                    <Button
                      size="compact-xs"
                      variant={s.waiting_for_check ? 'filled' : 'default'}
                      leftSection={<IconChecks size={13} />}
                      onClick={() => setChecking(s)}
                      data-testid="check-addresses"
                    >
                      Check addresses
                    </Button>
                  )}
                  {cancellable && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      leftSection={<IconX size={13} />}
                      onClick={() => cancel(s)}
                      data-testid="cancel-schedule"
                    >
                      {once ? 'Cancel' : 'Stop'}
                    </Button>
                  )}
                </Group>
              )}
            </Group>
          </Card>
        );
      })}
      {checking && (
        <PreflightModal
          workflowId={workflowId}
          scheduleId={checking.id}
          name={checking.at ? `once, ${onceWords(checking.at)}` : checking.sentence}
          onClose={() => setChecking(null)}
        />
      )}
    </Stack>
  );
}

function NewSend({
  workflowId, enabled, sendsEmail, recordType, dirty, oneOff,
}: { workflowId: string; enabled: boolean; sendsEmail: boolean; recordType: RecordType; dirty: boolean; oneOff: boolean }) {
  const v = useVocab();
  const campaigns = useCampaigns().data?.campaigns ?? [];
  const [source, setSource] = useState<'build' | 'saved'>('build');
  const choices = useSegmentChoices(source === 'saved');
  const [segKey, setSegKey] = useState<string | null>(null);
  const [built, setBuilt] = useState<BuiltSegment>(BLANK_SEGMENT);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [when, setWhen] = useState<When>('now');
  const [date, setDate] = useState(() => ukDate(1));
  const [time, setTime] = useState('10:00');
  const [weekday, setWeekday] = useState('0');
  const [reenrol, setReenrol] = useState(false);
  const [error, setError] = useState('');
  const [checkFor, setCheckFor] = useState<WorkflowEnrolSchedule | null>(null);
  const edits = useEnrolEdits();

  const saved = choices.find((c) => keyOf(c) === segKey) ?? null;
  const filter = useMemo(() => builtFilter(built), [built]);
  const segment: EnrolBody | null = source === 'saved'
    ? (saved ? { record_type: recordType, segment: { kind: saved.kind, id: saved.id ?? null }, filter: saved.filter as EnrolBody['filter'] } : null)
    : { record_type: recordType, segment: { kind: 'filter', id: null }, filter: filter as EnrolBody['filter'] };
  const preview = useEnrolPreview(workflowId, segment ? { ...segment, schedule: null } : null);

  const timeOk = TIME_OK.test(time);
  const dateOk = DATE_OK.test(date);
  const at = `${date}T${time.padStart(5, '0')}`;
  const past = when === 'once' && dateOk && timeOk && at < ukNow();
  const ready = !!segment && enabled && !dirty && (when === 'now' || (timeOk && (when !== 'once' || dateOk)));

  function body(): EnrolBody | null {
    if (!segment) return null;
    switch (when) {
      // An email goes through a send-once at this minute, so its addresses are checked first.
      case 'now': return { ...segment, schedule: sendsEmail ? { frequency: 'once', at: ukNow() } : null };
      case 'once': return { ...segment, schedule: { frequency: 'once', at } };
      case 'daily': return { ...segment, schedule: { frequency: 'daily', time, reenrol } };
      case 'weekly': return { ...segment, schedule: { frequency: 'weekly', weekday: Number(weekday), time, reenrol } };
    }
  }

  async function go() {
    const b = body();
    if (!b) return;
    setError('');
    try {
      const res = await edits.enrol.mutateAsync({ id: workflowId, body: b });
      if (res.schedule) {
        notifications.show({ color: 'teal', title: when === 'daily' || when === 'weekly' ? 'Schedule set' : 'Send set', message: res.schedule.sentence });
        if (res.schedule.waiting_for_check) setCheckFor(res.schedule);
      } else {
        notifications.show({ color: 'teal', title: 'Enrolling', message: `${res.preview.sentence} It happens in the background.` });
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const many = recordType === 'submission' ? v.many('lead') : v.many('person');
  return (
    <Card padding="sm" withBorder data-testid="new-send">
      <Stack gap="sm">
        <Text size="sm" fw={600}>{sendsEmail ? 'Send to a segment' : `Enrol ${many} from a segment`}</Text>
        <SegmentedControl
          size="xs"
          data={[{ value: 'build', label: 'Choose here' }, { value: 'saved', label: 'A saved segment' }]}
          value={source}
          onChange={(x) => setSource(x as 'build' | 'saved')}
          style={{ alignSelf: 'flex-start' }}
          data-testid="send-source"
        />
        {source === 'saved' ? (
          <Select
            size="xs"
            label="Segment"
            placeholder="Choose a segment"
            data={[
              { group: 'Mine', items: choices.filter((c) => c.kind === 'private').map((c) => ({ value: keyOf(c), label: c.name })) },
              { group: 'Shared with me', items: choices.filter((c) => c.kind === 'shared').map((c) => ({ value: keyOf(c), label: c.name })) },
            ].filter((g) => g.items.length > 0)}
            value={segKey}
            onChange={setSegKey}
            searchable
            data-testid="send-segment"
          />
        ) : (
          <Stack gap="xs">
            <MultiSelect
              size="xs"
              label={`${v.Many('lead')} from`}
              placeholder={`Every ${v.one('campaign')}`}
              searchable
              clearable
              data={campaigns.filter((c) => !c.is_reference_list).map((c) => ({ value: c.id, label: c.name }))}
              value={built.campaignIds}
              onChange={(campaignIds) => setBuilt((b) => ({ ...b, campaignIds }))}
              data-testid="send-streams"
            />
            <Group gap={6} align="center" wrap="wrap">
              <Text size="xs">Arrived in the last</Text>
              <NumberInput
                size="xs"
                aria-label="Arrived in the last this many days"
                placeholder="Any"
                min={1}
                max={MAX_DAYS}
                allowDecimal={false}
                allowNegative={false}
                clampBehavior="strict"
                value={built.days > 0 ? built.days : ''}
                onChange={(n) => setBuilt((b) => ({ ...b, days: typeof n === 'number' && n > 0 ? n : 0 }))}
                w={80}
                data-testid="send-days"
              />
              <Text size="xs">days (empty for any time)</Text>
            </Group>
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setRulesOpen((o) => !o)}
              style={{ alignSelf: 'flex-start' }}
              data-testid="send-rules-toggle"
            >
              {`Field rules${built.fieldFilters.length ? ` (${built.fieldFilters.length})` : ''}`}
            </Button>
            <Collapse in={rulesOpen}>
              {rulesOpen && (
                <RuleBuilder
                  filters={{
                    campaign_ids: built.campaignIds,
                    field_filters: built.fieldFilters,
                    ignore_campaign_filter: built.ignoreCampaigns || null,
                  }}
                  onChange={(patch) => setBuilt((b) => ({
                    ...b,
                    ...('field_filters' in patch ? { fieldFilters: patch.field_filters ?? [] } : {}),
                    ...('ignore_campaign_filter' in patch ? { ignoreCampaigns: !!patch.ignore_campaign_filter } : {}),
                  }))}
                  onEnter={() => undefined}
                  enabled={rulesOpen}
                />
              )}
            </Collapse>
          </Stack>
        )}
        {sendsEmail && (
          <Checkbox
            size="xs"
            checked
            disabled
            readOnly
            label="Only people who agreed to emails"
            description="Always on when it sends an email. One email per address, to its newest arrival."
            data-testid="send-consent"
          />
        )}

        <Stack gap={6}>
          <Text size="xs" fw={500}>When</Text>
          <SegmentedControl
            size="xs"
            data={[
              { value: 'now', label: 'Now' },
              { value: 'once', label: oneOff ? 'At a time' : 'Once at' },
              ...(oneOff ? [] : [
                { value: 'daily', label: 'Every day' },
                { value: 'weekly', label: 'Every week' },
              ]),
            ]}
            value={when}
            onChange={(x) => setWhen(x as When)}
            style={{ alignSelf: 'flex-start' }}
            data-testid="send-when"
          />
          {when !== 'now' && (
            <Group gap="xs" align="flex-end" wrap="wrap">
              {when === 'once' && (
                <TextInput
                  size="xs"
                  type="date"
                  label="On"
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value)}
                  error={dateOk ? undefined : 'Choose a date'}
                  w={150}
                  data-testid="send-date"
                />
              )}
              {when === 'weekly' && (
                <Select
                  size="xs"
                  label="On"
                  data={DAYS.map((d, i) => ({ value: String(i), label: d }))}
                  value={weekday}
                  allowDeselect={false}
                  onChange={(x) => x && setWeekday(x)}
                  w={140}
                  data-testid="send-weekday"
                />
              )}
              <TextInput
                size="xs"
                type="time"
                label="At (UK time)"
                value={time}
                onChange={(e) => setTime(e.currentTarget.value)}
                error={timeOk ? undefined : 'HH:MM'}
                w={120}
                data-testid="send-time"
              />
            </Group>
          )}
          {(when === 'daily' || when === 'weekly') && (
            <Switch
              size="xs"
              label="Every time, everyone who matches"
              description={`Off: each time, only ${many} who newly match. Each person still gets an email once from this ${v.one('workflow')}.`}
              checked={reenrol}
              onChange={(e) => setReenrol(e.currentTarget.checked)}
              data-testid="send-reenrol"
            />
          )}
          {past && <Text size="xs" c="orange">That time has passed: it goes as soon as it can.</Text>}
          {sendsEmail && (when === 'now' || when === 'once') && (
            <Text size="xs" c="dimmed">
              Its addresses are checked before it goes. Newest first, up to 500,000.
            </Text>
          )}
        </Stack>

        <PreviewCounts preview={preview} hasSegment={!!segment} sendsEmail={sendsEmail} />
        {!enabled && (
          <Text size="xs" c="orange" data-testid="send-switch-on">{`Switch the ${v.one('workflow')} on first: one that is off sends nothing.`}</Text>
        )}
        {enabled && dirty && (
          <Text size="xs" c="orange">Save your changes first: a send uses the saved version.</Text>
        )}
        {error && <Alert color="red" variant="light" p="xs"><Text size="sm">{error}</Text></Alert>}
        <Group justify="flex-end">
          <Button
            size="xs"
            leftSection={when === 'now' ? <IconSend size={14} /> : <IconCalendarEvent size={14} />}
            disabled={!ready || !preview.data}
            loading={edits.enrol.isPending}
            onClick={go}
            data-testid="send-go"
          >
            {when === 'now'
              ? (sendsEmail ? 'Send now' : `Enrol ${many} now`)
              : when === 'once' ? `Send on ${onceWords(at)}` : 'Set the schedule'}
          </Button>
        </Group>
      </Stack>
      {checkFor && (
        <PreflightModal
          workflowId={workflowId}
          scheduleId={checkFor.id}
          name={checkFor.at ? `once, ${onceWords(checkFor.at)}` : checkFor.sentence}
          onClose={() => setCheckFor(null)}
        />
      )}
    </Card>
  );
}

function Count({ label, n, testId }: { label: string; n: number | null | undefined; testId: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={700} data-testid={testId}>{formatNumber(n ?? 0)}</Text>
    </Stack>
  );
}

function PreviewCounts({
  preview, hasSegment, sendsEmail,
}: {
  preview: { data?: EnrolPreview; isPending: boolean; error: Error | null };
  hasSegment: boolean;
  sendsEmail: boolean;
}) {
  const p = preview.data;
  return (
    <Alert variant="light" color={preview.error ? 'red' : 'chartBlue'} p="xs" data-testid="send-preview">
      {!hasSegment ? (
        <Text size="sm">Choose a segment to see how many.</Text>
      ) : preview.isPending ? (
        <Group gap="xs"><Loader size="xs" /><Text size="sm">Counting</Text></Group>
      ) : preview.error ? (
        <Text size="sm">{preview.error.message}</Text>
      ) : p ? (
        <Stack gap={6}>
          <Text size="sm" fw={600}>{p.sentence}</Text>
          {sendsEmail && p.with_consent != null && (
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs" verticalSpacing={4}>
              <Count label="Agreed to emails" n={p.with_consent} testId="count-consent" />
              <Count label="On the never-email list" n={p.on_never_email} testId="count-never" />
              <Count label="Already checked" n={p.already_checked} testId="count-checked" />
              <Count label="Failed their check" n={p.failed_check} testId="count-failed" />
              <Count label="Still to check" n={p.needs_checking} testId="count-to-check" />
              <Count label="Will be sent" n={p.will_send} testId="count-will-send" />
            </SimpleGrid>
          )}
          {p.send_warning && <Text size="xs" c="orange" data-testid="send-warning">{p.send_warning}</Text>}
        </Stack>
      ) : null}
    </Alert>
  );
}
